import { type DragEvent, useId, useRef, useState } from "react";
import { cn } from "../lib/cn";
import { Icon } from "./icon";

/** Файл в списке: своя строка состояния у каждого. */
export interface PickedFile {
	readonly id: string;
	readonly name: string;
	readonly sizeLabel: string;
	/** 0…100. `null` — загрузка ещё не началась либо уже не идёт. */
	readonly progress?: number | null;
	/** Готов к отправке: полоса уступает место галочке. */
	readonly done?: boolean;
	/** Текст ошибки. Показывается вместо полосы — файл остаётся в списке. */
	readonly error?: string | null;
}

export interface FilePickerProps {
	files: readonly PickedFile[];
	onSelect: (files: File[]) => void;
	onRemove: (id: string) => void;
	/** `image/*,video/*` и подобное — уходит в `accept` и в подсказку. */
	accept: string;
	/** Подпись зоны: «Перетащите файлы или выберите на устройстве». */
	label: string;
	/** Строка под зоной: форматы и потолки. Обязательна. */
	hint: string;
	/** Подпись крестика у строки файла — читает скринридер. */
	removeLabel: string;
	/** Достигнут потолок: зона гаснет, но список остаётся. */
	disabled?: boolean;
	/** Сообщение поверх зоны, когда добавить больше нельзя. */
	disabledHint?: string;
	className?: string;
}

/**
 * Выбор файлов: зона перетаскивания, список выбранного, полоса загрузки.
 *
 * Прогресс здесь не украшение. Вложение обращения в поддержку — до 100 МБ
 * видео, и загрузка идёт прямо в хранилище: без полосы интерфейс на минуту
 * выглядит зависшим ровно у того человека, у которого уже что-то не работает.
 *
 * Все подписи — обязательные пропы: у кита нет текстовых умолчаний, иначе
 * умолчание одного языка уезжает в интерфейс на другом.
 */
export function FilePicker({
	files,
	onSelect,
	onRemove,
	accept,
	label,
	hint,
	removeLabel,
	disabled = false,
	disabledHint,
	className,
}: FilePickerProps) {
	const inputRef = useRef<HTMLInputElement>(null);
	const inputId = useId();
	const [dragging, setDragging] = useState(false);

	const pick = (list: FileList | null) => {
		if (!list || list.length === 0) return;
		onSelect(Array.from(list));
		// Сброс значения: без него повторный выбор ТОГО ЖЕ файла не вызывает
		// `change`, и человек считает, что интерфейс его не услышал.
		if (inputRef.current) inputRef.current.value = "";
	};

	const onDrop = (event: DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		setDragging(false);
		if (!disabled) pick(event.dataTransfer.files);
	};

	return (
		<div className={cn("flex flex-col gap-3", className)}>
			<div
				onDragOver={(event) => {
					event.preventDefault();
					if (!disabled) setDragging(true);
				}}
				onDragLeave={() => setDragging(false)}
				onDrop={onDrop}
				className={cn(
					"flex flex-col items-center gap-1.5 rounded-card border border-dashed px-5 py-6 text-center transition-colors duration-[var(--dur-hover)]",
					dragging ? "border-brand bg-sunken" : "border-hairline bg-sunken",
					disabled && "opacity-60",
				)}
			>
				<Icon name="Paperclip" className="text-fg-muted" />
				<label
					htmlFor={inputId}
					className={cn(
						"t-meta text-fg-primary underline decoration-transparent underline-offset-4 transition-colors duration-[var(--dur-hover)]",
						disabled ? "cursor-not-allowed" : "cursor-pointer hover:decoration-current",
					)}
				>
					{label}
				</label>
				<p className="t-caption text-fg-muted">{disabled ? (disabledHint ?? hint) : hint}</p>
				<input
					ref={inputRef}
					id={inputId}
					type="file"
					multiple
					accept={accept}
					disabled={disabled}
					className="sr-only"
					onChange={(event) => pick(event.target.files)}
				/>
			</div>

			{files.length > 0 && (
				<ul className="flex flex-col gap-1.5">
					{files.map((file) => (
						<li
							key={file.id}
							className="flex items-center gap-3 rounded-control bg-sunken px-3 py-2.5"
						>
							<div className="flex min-w-0 flex-1 flex-col gap-1">
								<span className="t-meta truncate text-fg-primary">{file.name}</span>
								{file.error ? (
									<span className="t-caption text-danger">{file.error}</span>
								) : (
									<span className="t-caption text-fg-muted">{file.sizeLabel}</span>
								)}
								{typeof file.progress === "number" && !file.done && !file.error && (
									<div className="h-1 w-full overflow-hidden rounded-full bg-chip">
										<div
											className="h-full rounded-full bg-brand transition-[width] duration-[var(--dur-react)]"
											style={{ width: `${Math.min(100, Math.max(0, file.progress))}%` }}
										/>
									</div>
								)}
							</div>
							{file.done && !file.error && <Icon name="Check" className="text-fg-muted" />}
							<button
								type="button"
								onClick={() => onRemove(file.id)}
								aria-label={removeLabel}
								className="iec-press-glyph tap-target focus-ring rounded-full text-fg-muted"
							>
								<Icon name="X" />
							</button>
						</li>
					))}
				</ul>
			)}
		</div>
	);
}
